/**
 * VP-18765 — shipping address field length limits.
 *
 * The limits are the narrowest column each value is copied into on the
 * shipping side today: LIS-Shipping's lis_inventory tables (pickup_schedules,
 * client_transaction_shipping, client_consumables_transaction,
 * pending_kit_requests, ...). A value longer than the column is rejected by
 * MySQL on the write ("value too long for column"), which is how LIS-7879 lost
 * a kit order to a 47-character city. The FE ticket's example copy — "City must
 * be 45 characters or fewer." — comes from that 45-char city column.
 *
 * VP-18764 (lis-shipping `feature/unify-address-length`, on `dev`) widens all
 * of those columns to Core v1's VARCHAR(191); once it is on production every
 * entry below can be raised to SHIPPING_ADDRESS_MAX_BYTES in one edit.
 *
 * Length is measured in UTF-8 bytes, matching the backend ticket, so a value
 * that passes here cannot fail there.
 *
 * Carrier label limits (FedEx 35-char street lines, etc.) are a separate layer
 * handled by the shipping backend at label-build time and are NOT enforced
 * here.
 */

/** Core v1's address column width — the ceiling every limit below moves to after VP-18764. */
export const SHIPPING_ADDRESS_MAX_BYTES = 191;

/**
 * Per-field limits keyed by the form-model names the address forms use
 * (lis_inventory column widths before VP-18764, narrowest table wins).
 */
export const SHIPPING_ADDRESS_FIELD_LIMITS = Object.freeze({
  recipientName: 45, // pickup_schedules.name, client_transaction_shipping.customer_name
  companyName: 45,
  streetAddress: 150, // client_transaction_shipping.customer_street
  addressLine2: 64, // *.apt_po (also the DTOs' @MaxLength on master)
  city: 45,
  state: 45, // pickup_schedules.state_or_province, customer_state
  zipcode: 45, // pickup_schedules.postal_code, customer_zipcode
  country: 45, // pickup_schedules.country_code
  phone: 45, // pickup_schedules.phone_number, customer_office_phone
});

const encoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

/** UTF-8 byte length of a value (`null`/`undefined` → 0). */
export function utf8ByteLength(value) {
  if (value === null || value === undefined) return 0;
  const str = String(value);
  if (encoder) return encoder.encode(str).length;
  // Fallback for environments without TextEncoder (old jsdom).
  return unescape(encodeURIComponent(str)).length;
}

export function getShippingFieldLimit(field) {
  return SHIPPING_ADDRESS_FIELD_LIMITS[field] || SHIPPING_ADDRESS_MAX_BYTES;
}

/** True when `value` is longer than the limit for `field`. */
export function exceedsShippingFieldLimit(field, value) {
  return utf8ByteLength(value) > getShippingFieldLimit(field);
}

/**
 * The fields of `address` that exceed their limit, as { field: limit }.
 * Only keys present in `address` (and known to the limits map) are checked, so
 * callers can pass their whole form model.
 */
export function getShippingAddressLengthErrors(address) {
  const errors = {};
  if (!address) return errors;
  Object.keys(SHIPPING_ADDRESS_FIELD_LIMITS).forEach((field) => {
    if (field in address && exceedsShippingFieldLimit(field, address[field])) {
      errors[field] = getShippingFieldLimit(field);
    }
  });
  return errors;
}

export function hasShippingAddressLengthError(address) {
  return Object.keys(getShippingAddressLengthErrors(address)).length > 0;
}

/**
 * Error copy from the ticket: "City must be 45 characters or fewer."
 */
export function formatShippingLengthMessage(fieldLabel, limit = SHIPPING_ADDRESS_MAX_BYTES) {
  return `${fieldLabel} must be ${limit} characters or fewer.`;
}

/**
 * Pull field-level messages out of a LIS-Shipping 400. Nest's ValidationPipe
 * answers `{ message: ["city must be shorter than or equal to 191 characters", ...] }`,
 * each message starting with the DTO property. `wireToField` maps those
 * property names (e.g. `apt_po`) onto the form-model field names so the form
 * can show them inline. Messages that don't start with a mapped property
 * (e.g. "Internal server error") are ignored, so the result is {} unless the
 * backend named a field the form knows.
 */
export function parseShippingFieldErrors(error, wireToField = {}) {
  const messages = error?.response?.data?.message;
  const list = Array.isArray(messages) ? messages : (typeof messages === 'string' ? [messages] : []);
  const errors = {};
  list.forEach((msg) => {
    const match = /^([A-Za-z_][A-Za-z0-9_]*)\s/.exec(String(msg));
    const field = match && wireToField[match[1]];
    if (field) errors[field] = String(msg);
  });
  return errors;
}
